import { MatchingRelationType } from "PFApp/constants/matching_relation_type";
import { IMatchScores } from "PFTypes";
import React, { createContext, PropsWithChildren, useCallback, useContext, useState } from "react";

import { MarketplaceModalType } from "../constants";
import { useTabData } from "../hooks/use_tab_data";
import { FiltersData, MarketplaceContextData, OrderData, SelectedRoleId } from "./marketplace_context.types";

const MarketplaceContext = createContext<MarketplaceContextData | null>(null);

type MarketplaceContextProviderProps = PropsWithChildren;

const defaultFiltersData: FiltersData = {};
const defaultOrderData: OrderData = null;

export const MarketplaceContextProvider = ({ children }: MarketplaceContextProviderProps) => {
  const [modalType, setModalType] = useState<MarketplaceModalType>(null);
  const [modalData, setModalData] = useState<MarketplaceContextData["modal"]["data"]>(null);
  const [selectedRoleId, setSelectedRoleId] = useState<SelectedRoleId>(null);
  const [selectedRoleScores, setSelectedRoleScores] = useState<IMatchScores | null>(null);
  const [relationType, setRelationType] = useState<MatchingRelationType>(
    MatchingRelationType.PrefilteredAndAvailable
  );
  const [getFiltersData, setFiltersData] = useTabData<FiltersData>(defaultFiltersData);
  const [getOrderData, setOrderData] = useTabData<OrderData>(defaultOrderData);

  const changeModalType: MarketplaceContextData["modal"]["setType"] = useCallback(
    (mode: MarketplaceModalType) => {
      setModalType(mode);
    },
    []
  );

  const changeRelationType: MarketplaceContextData["setRelationType"] = useCallback((relationType) => {
    setRelationType(relationType);
  }, []);

  const changeSelectedRole: MarketplaceContextData["setSelectedRole"] = useCallback((id, scores = null) => {
    setSelectedRoleId(id);
    setSelectedRoleScores(scores);
  }, []);

  return (
    <MarketplaceContext.Provider
      value={{
        modal: {
          type: modalType,
          setType: changeModalType,
          data: modalData,
          setData: setModalData
        },
        selectedRoleId,
        selectedRoleScores,
        setSelectedRole: changeSelectedRole,
        relationType,
        setRelationType: changeRelationType,
        getFiltersData,
        getOrderData,
        setOrderData,
        setFiltersData
      }}
    >
      {children}
    </MarketplaceContext.Provider>
  );
};

export const useMarketplaceContext = () => {
  const context = useContext(MarketplaceContext);

  if (!context) {
    throw new Error("useMarketplaceContext must be used within a MarketplaceContextProvider");
  }

  return context;
};
